import { supabase } from '../lib/supabase';

export const emailService = {
  /**
   * Send appointment confirmation email
   */
  async sendAppointmentConfirmation(appointmentData, helperData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user profile for email
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', user?.id)
        ?.single();

      if (!userProfile?.email) {
        throw new Error('User email not found');
      }

      // Call Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-appointment-confirmation', {
        body: {
          appointmentData: {
            appointmentDate: appointmentData?.appointmentDate,
            appointmentTime: appointmentData?.appointmentTime,
            duration: appointmentData?.duration,
            sessionType: appointmentData?.sessionType,
            topic: appointmentData?.topic
          },
          userData: {
            email: userProfile?.email,
            fullName: userProfile?.full_name
          },
          helperData: {
            name: helperData?.name,
            typeLabel: helperData?.typeLabel
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Send appointment confirmation error:', error);
      return { data: null, error };
    }
  },

  /**
   * Send crisis resources email
   */
  async sendCrisisResources() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user profile for email
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', user?.id)
        ?.single();

      if (!userProfile?.email) {
        throw new Error('User email not found');
      }

      // Call Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-crisis-resources', {
        body: {
          userEmail: userProfile?.email,
          userName: userProfile?.full_name
        }
      });

      if (error) {
        // Gracefully handle all Edge Function errors
        console.warn('Edge Function error (crisis resources):', error?.message || error);
        return { data: null, error: null, notDeployed: true };
      }
      return { data, error: null };
    } catch (error) {
      console.error('Send crisis resources error:', error);
      // Return gracefully for all errors - don't disrupt user experience
      return { data: null, error: null, notDeployed: true };
    }
  },

  /**
   * Get email notification history
   */
  async getEmailHistory(limit = 20) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('email_notifications')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('sent_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      // Convert snake_case to camelCase
      const notifications = data?.map(notification => ({
        id: notification?.id,
        userId: notification?.user_id,
        emailType: notification?.email_type,
        recipientEmail: notification?.recipient_email,
        sentAt: notification?.sent_at,
        status: notification?.status,
        errorMessage: notification?.error_message,
        metadata: notification?.metadata
      })) || [];

      return { data: notifications, error: null };
    } catch (error) {
      console.error('Get email history error:', error);
      return { data: [], error };
    }
  },

  /**
   * Send intake progress PDF via email
   */
  async sendIntakeProgressPDF(pdfBlob, pathway) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user profile for email
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', user?.id)
        ?.single();

      if (!userProfile?.email) {
        throw new Error('User email not found');
      }

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader?.readAsDataURL(pdfBlob);
      const base64PDF = await base64Promise;

      // Call Edge Function (using existing Resend integration)
      const { data, error } = await supabase?.functions?.invoke('send-intake-progress', {
        body: {
          userEmail: userProfile?.email,
          userName: userProfile?.full_name,
          pathway: pathway === 'quick' ? 'Quick Start' : 'Comprehensive Questionnaire',
          pdfAttachment: base64PDF,
          fileName: `intake-progress-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`
        }
      });

      if (error) {
        console.warn('Edge Function error (intake progress):', error?.message || error);
        return { data: null, error: error, notDeployed: true };
      }
      return { data, error: null };
    } catch (error) {
      console.error('Send intake progress PDF error:', error);
      return { data: null, error, notDeployed: false };
    }
  }
};